import { useRef, useEffect, useState } from 'react';
import { bindLegacySignatureInput } from './legacy-signature-input';

type Props = {
  mode: 'draw' | 'upload';
  value: string | null;
  onChange: (v: string | null) => void;
};

/**
 * 电子签字段：mode=draw 用 canvas 手写；mode=upload 用文件选择
 * 输出：base64 字符串
 */
export default function SignaturePad({ mode, value, onChange }: Props) {
  if (mode === 'upload') return <UploadMode value={value} onChange={onChange} />;
  return <DrawMode value={value} onChange={onChange} />;
}

/**
 * 手写模式：内嵌处只做预览/入口，点击进入全屏签名板（手机端可全屏书写，防误触滚动）。
 * 0907：签名板改为横版——竖屏手机上整块旋转 90°，让填写者横握手机从左往右签，签出来的图也是横的。
 */
function DrawMode({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="sig-pad">
      {value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <img src={value} alt="签名预览" className="sig-pad-preview" />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" onClick={() => setOpen(true)}>重新签名</button>
            <button type="button" className="btn" onClick={() => onChange(null)}>清空</button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn sig-pad-entry" onClick={() => setOpen(true)}>
          ✍ 点击进入横版全屏签名
        </button>
      )}
      {open && (
        <FullscreenSign
          onCancel={() => setOpen(false)}
          onConfirm={(dataUrl) => {
            onChange(dataUrl);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** 视口尺寸（随旋转 / 缩放变化） */
function useViewport() {
  const read = () => ({ w: window.innerWidth, h: window.innerHeight });
  const [vp, setVp] = useState(read);
  useEffect(() => {
    const on = () => setVp(read());
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('orientationchange', on);
    };
  }, []);
  return vp;
}

/**
 * 横版全屏签名板：铺满视口、锁定页面滚动，适配手机高分屏（DPR 缩放）。
 * 竖屏（高 > 宽）时把整块板子 rotate(90deg)，板子的"横向"沿屏幕长边铺开——填写者横握手机，从左往右签；
 * 指针坐标按旋转反算回画布坐标，导出的 PNG 就是正常的横向签名图。横屏 / 桌面端不旋转。
 */
function FullscreenSign({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: (dataUrl: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const vp = useViewport();
  const rotated = vp.h > vp.w;
  // 板子自身的宽高（旋转后：宽 = 屏幕高，高 = 屏幕宽）
  const boxW = rotated ? vp.h : vp.w;
  const boxH = rotated ? vp.w : vp.h;

  useEffect(() => {
    // 锁定背景滚动
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // 画布初始化：尺寸随视口 / 旋转变化重建（重建会清空笔迹）
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // 旋转后 getBoundingClientRect 是屏幕上的外接框：宽高互换
    const localW = rotated ? rect.height : rect.width;
    const localH = rotated ? rect.width : rect.height;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.max(1, Math.round(localW * dpr));
    canvas.height = Math.max(1, Math.round(localH * dpr));
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, localW, localH);
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    drawingRef.current = false;
    setHasInk(false);
  }, [rotated, boxW, boxH]);

  // 屏幕坐标 → 画布坐标；旋转时：画布 x 沿屏幕向下，画布 y 沿屏幕向左
  const pos = (e: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    if (!rotated) return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    return { x: e.clientY - rect.top, y: rect.right - e.clientX };
  };
  const start = (e: { clientX: number; clientY: number; pointerId?: number }) => {
    drawingRef.current = true;
    if (e.pointerId !== undefined) canvasRef.current!.setPointerCapture?.(e.pointerId);
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1); // 点按也留痕
    ctx.stroke();
    setHasInk(true);
  };
  const move = (e: { clientX: number; clientY: number }) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => { drawingRef.current = false; };
  useEffect(() => {
    if ('PointerEvent' in window || !canvasRef.current) return;
    return bindLegacySignatureInput(canvasRef.current, { start, move, end });
  }, [rotated, boxW, boxH]);
  const clear = () => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const localW = rotated ? rect.height : rect.width;
    const localH = rotated ? rect.width : rect.height;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, localW, localH);
    setHasInk(false);
  };

  return (
    <div
      className={`sig-fullscreen${rotated ? ' is-rotated' : ''}`}
      style={{ width: boxW, height: boxH }}
      role="dialog"
      aria-label="横版全屏签名"
    >
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', gap: 12 }}>
        <strong style={{ fontSize: 15, color: '#1f2937' }}>{rotated ? '请横握手机，在空白区域从左往右签名' : '请在下方空白区域从左往右签名'}</strong>
        <button type="button" className="btn" onClick={onCancel}>✕ 关闭</button>
      </div>
      <div style={{ flex: 1, position: 'relative', background: '#f4f5f7', padding: 10, minHeight: 0 }}>
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          style={{
            touchAction: 'none', width: '100%', height: '100%', display: 'block',
            background: '#fff', border: '1px dashed #cbd2dd', borderRadius: 10, cursor: 'crosshair',
          }}
        />
        {!hasInk && (
          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c2c8d4', fontSize: 22, pointerEvents: 'none', letterSpacing: 4 }}>
            签 名 区
          </span>
        )}
      </div>
      <div style={{ padding: '10px 16px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb' }}>
        <button type="button" className="btn" onClick={clear} disabled={!hasInk}>清空重写</button>
        <button type="button" className="btn" onClick={onCancel}>取消</button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!hasInk}
          onClick={() => onConfirm(canvasRef.current!.toDataURL('image/png'))}
        >
          确认签名
        </button>
      </div>
    </div>
  );
}

function UploadMode({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('图片不能超过 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // 小图直接用；大图等比缩到 1024px 内并转 JPEG，避免 base64 撑爆请求体
      if (file.size <= 300 * 1024) {
        onChange(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        const max = 1024;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff'; // PNG 透明底转 JPEG 需铺白
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        onChange(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => alert('图片读取失败，请换一张试试');
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="sig-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: 'none' }}
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
      <button type="button" className="btn" onClick={() => inputRef.current?.click()}>
        选择签名图片
      </button>
      {value && (
        <div className="sig-preview">
          <img src={value} alt="签名" style={{ maxWidth: 320, border: '1px solid var(--border)' }} />
          <button type="button" className="icon-btn icon-danger" onClick={() => onChange(null)} title="删除">
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
